import { useQueries, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { pick } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { frozenArr } from '~/common/arrayUtils'
import { ONE_HOUR } from '~/common/cache'
import fetch from '~/common/fetch'
import { FetchOptions } from '~/common/fetchHelpers'
import { defineLocalIndexedDBStore } from '~/common/indexedDBUtils'
import createDebugLogger from '~/common/logger'
import { getLocalStorageJSON, setLocalStorageWithTTL } from '~/common/react-query/useQueryWithLocalStoragePersist'
import { Expression, Locale } from '~/common/valeyard/runtime'
import { useHasChanged } from '~/hooks/usePrevious'

const debug = createDebugLogger('l10nQuery')

export type Localization = {
  locale: string
  languageTag: string
  catalog: Record<string, string>
}

interface L10nLayerMeta {
  timezone: string
  locale: string
  locale_tag: string
  l10n_search_locales: string[]
  l10n_search_locales_tags: string[]
  l10n_base_hash: string
  l10n_tenant_hash: string
}

interface LocalizationLayer {
  catalog: Record<string, Expression>
  hash: string
  locale: string
  locale_tag: string
  layer_type: 'tenant' | 'base'
}

type L10nKnownTag = 'vndly' | 'anonymous'
type L10nLayerParams = { locale: string; layerType: 'tenant' | 'base'; tag: L10nKnownTag; hash: string }

const l10nApi = {
  layerMeta: (fetchOpts?: FetchOptions): Promise<L10nLayerMeta> => fetch.get('/api/v2/l10n/layers/meta', fetchOpts),
  layer: ({ locale, layerType, tag, hash }: L10nLayerParams, fetchOpts?: FetchOptions) =>
    fetch.get(`/api/v2/l10n/v/catalog/${layerType}/${locale}/${tag}?hash=${hash}`, fetchOpts)
}

/**
 * Fetches l10n messages from the api and uses a local storage and indexeddb as
 * a backup placeholder for the l10n data for quicker page loads
 *
 * @param anonymous - whether the user is anonymous or not
 */
export function useL10nMessages({ anonymous }: { anonymous: boolean }) {
  // Fetch layer meta from the local storage as a backup placeholder while the api is loading
  const l10nMetaFromStorage = useL10nLayerMetaFromLocalStorage()

  // Fetch layer meta from the api as the source of truth
  const l10nLayerMetaQuery = useL10nLayerMetaQuery()

  debug('-----------------')
  debug('useL10nMessagesQuery', { anonymous, l10nMetaFromStorage, l10nLayerMetaQuery: l10nLayerMetaQuery.data })

  // Backup the layer meta from the api to local storage so it always has the latest
  // for the next page load placeholder
  useSaveLayerMetaToLocalStorage(l10nLayerMetaQuery)

  const l10n_search_locales =
    l10nLayerMetaQuery.data?.l10n_search_locales ?? l10nMetaFromStorage?.l10n_search_locales ?? (frozenArr as string[])
  const locale_tag = l10nLayerMetaQuery.data?.locale_tag ?? l10nMetaFromStorage?.locale_tag ?? ''
  const timezone = l10nLayerMetaQuery.data?.timezone ?? l10nMetaFromStorage?.timezone ?? ''
  const tag = anonymous ? ('anonymous' as const) : ('vndly' as const)

  const l10nLayersFromDB = useL10nLayersFromDB(l10n_search_locales)
  const layersQueries = useL10nLayersQueries({
    l10n_search_locales,
    tag,
    l10nLayerMeta: l10nLayerMetaQuery.data,
    l10nLayerPlaceholderData: l10nLayersFromDB.data,
    placeholderDataReady: l10nLayersFromDB.allResolved
  })

  // Store fetch statuses into a simple hash of 0s (not fetch) and 1s (fetched), so we can compare them to the previous render:
  // e.g. 1001 means the first and fourth layers are fetched, but the rest are not
  const layerFetchedStatusesSimpleHash = layersQueries.map(layer => Number(layer.isFetched)).join('')

  const allLayeredLoaded = layersQueries.length > 0 && layersQueries.every(layer => layer.isSuccess)

  // Reduce all layers into a single catalog
  const reducedMessages = useMergeAllLayerMessages(allLayeredLoaded, layerFetchedStatusesSimpleHash, layersQueries)

  useSaveLayerQueriesToLocalDB(layerFetchedStatusesSimpleHash, layersQueries, tag)

  return {
    allLayeredLoaded,
    messages: reducedMessages,
    locale_tag,
    timezone
  }
}

export const l10nKeys = {
  all: ['l10n', 'catalog'] as const,
  layer: ['l10n', 'layer'] as const
}

export const useL10nLayerMetaQuery = (opts: UseQueryOptions<L10nLayerMeta> = {}) => {
  return useQuery({
    queryKey: [...l10nKeys.layer, 'meta'],
    queryFn: () => {
      debug('useL10nLayerMetaQuery -> l10nApi.layerMeta() -> API CALLED...')
      return l10nApi.layerMeta()
    },
    staleTime: Infinity,
    ...opts
  })
}

const L10N_LAYER_META_KEY = 'l10n-layer-meta'

const l10nLayerDB = defineLocalIndexedDBStore<LocalizationLayer>({ dbName: 'l10n', storeName: 'layers', version: 1 })

/**
 * Fetches l10n meta from indexedDB, if it exists
 * It's used as a stale placeholder as the API is fetched
 */
function useL10nLayerMetaFromLocalStorage() {
  return getLocalStorageJSON<Pick<L10nLayerMeta, 'l10n_search_locales' | 'locale_tag' | 'timezone'>>(
    L10N_LAYER_META_KEY
  )
}

function useSaveLayerMetaToLocalStorage(l10nLayerMetaQuery: UseQueryResult<L10nLayerMeta>) {
  useMemo(() => {
    if (l10nLayerMetaQuery.isSuccess) {
      debug('useSaveLayerMetaToLocalStorage::setLocalStorageWithTTL', l10nLayerMetaQuery.data)
      setLocalStorageWithTTL(
        L10N_LAYER_META_KEY,
        pick(l10nLayerMetaQuery.data, ['l10n_search_locales', 'locale_tag', 'timezone']),
        ONE_HOUR
      )
    }
  }, [l10nLayerMetaQuery.isSuccess, l10nLayerMetaQuery.data])
}

type L10nLayersFromDBRecord = Record<Locale, { tenant?: LocalizationLayer; base?: LocalizationLayer }>

function useL10nLayersFromDB(l10n_search_locales: string[]) {
  const [allResolved, setAllResolved] = useState(false)
  const [data, setData] = useState<L10nLayersFromDBRecord | undefined>(undefined)

  useEffect(() => {
    if (!l10n_search_locales || l10n_search_locales.length === 0) {
      return
    }

    const allPromises = l10n_search_locales.flatMap(locale => {
      const tenantKey = makeLayerKey({ layer_type: 'tenant', locale, tag: 'vndly' })
      const tenantLocalStorageData = l10nLayerDB.getValue(tenantKey).then(
        function success(layer) {
          debug('useL10nLayersFromDB::l10nLayerDB.getValue SUCCESS', tenantKey, layer)
          setData(prev => ({
            ...prev,
            [locale]: (prev && { ...prev[locale], tenant: layer }) || { tenant: layer }
          }))
        },
        function error(err) {
          debug('useL10nLayersFromDB::l10nLayerDB.getValue ERROR', tenantKey, err)
        }
      )
      debug('useL10nLayersFromDB::l10nLayerDB.getValue...', tenantKey)

      const baseKey = makeLayerKey({ layer_type: 'base', locale, tag: 'vndly' })
      const baseLocalStorageData = l10nLayerDB.getValue(baseKey).then(
        function success(layer) {
          debug('useL10nLayersFromDB::l10nLayerDB.getValue SUCCESS', baseKey, layer)
          setData(prev => ({
            ...prev,
            [locale]: (prev && { ...prev[locale], base: layer }) || { base: layer }
          }))
        },
        function error(err) {
          debug('useL10nLayersFromDB::l10nLayerDB.getValue ERROR', baseKey, err)
        }
      )
      debug('useL10nLayersFromDB::l10nLayerDB.getValue...', baseKey)

      return [tenantLocalStorageData, baseLocalStorageData]
    })

    Promise.all(allPromises).then(() => {
      debug('useL10nLayersFromDB::allPromises resolved')
      setAllResolved(true)
    })
  }, [l10n_search_locales.join(',')])

  return { data, allResolved }
}

function useSaveLayerQueriesToLocalDB(
  layerFetchedStatusesSimpleHash: string,
  layerQueries: UseQueryResult<LocalizationLayer>[],
  tag: L10nKnownTag
) {
  // Store fetch statuses into a simple hash of 0s (not fetch) and 1s (fetched), so we can compare them to the previous render:
  // e.g. 1001 means the first and fourth layers are fetched, but the rest are not
  const anyFetchedStatusHasChanged = useHasChanged(layerFetchedStatusesSimpleHash)

  // Store each layer query's data in local storage only after they are fetched, so we always have the latest from the server
  if (anyFetchedStatusHasChanged && layerQueries.length > 0) {
    layerQueries.forEach(layer => {
      if (layer.isFetched && layer.data) {
        const { layer_type, locale } = layer.data
        debug('useSaveLayerQueriesToLocalDB::l10nLayerDB.setValue', layerFetchedStatusesSimpleHash)
        l10nLayerDB.setValue(makeLayerKey({ layer_type, locale, tag }), layer.data).then(
          function success() {
            debug('-- useSaveLayerQueriesToLocalDB::l10nLayerDB.setValue SUCCESS', layer.data)
          },
          function error(err) {
            debug('-- useSaveLayerQueriesToLocalDB::l10nLayerDB.setValue ERROR', err)
          }
        )
      }
    })
  }
  return layerFetchedStatusesSimpleHash
}

function useMergeAllLayerMessages(
  allLayeredLoaded: boolean,
  layerFetchedStatusesSimpleHash: string,
  layerQueries: UseQueryResult<LocalizationLayer>[]
) {
  // Reduce all layers into a single catalog, once right away when we load layers from local storage,
  // and again when all layers are fetched
  return useMemo(() => {
    debug('useMergeAllLayerMessages::reducer', { allLayeredLoaded, layerFetchedStatusesSimpleHash })
    return allLayeredLoaded
      ? layerQueries.reduceRight((acc, layer) => {
          return {
            ...acc,
            ...layer.data?.catalog
          }
        }, {})
      : {}
  }, [allLayeredLoaded, layerFetchedStatusesSimpleHash])
}

function useL10nLayersQueries({
  l10n_search_locales,
  tag,
  l10nLayerMeta,
  l10nLayerPlaceholderData,
  placeholderDataReady
}: {
  l10n_search_locales: string[]
  tag: L10nKnownTag
  l10nLayerMeta?: L10nLayerMeta
  l10nLayerPlaceholderData?: L10nLayersFromDBRecord
  placeholderDataReady: boolean
}) {
  const layerQueryOptsArr = useMemo(() => {
    if (!placeholderDataReady) {
      return []
    }

    const queryOpts = l10n_search_locales.flatMap(locale => {
      const tenantLayerQueryOptions = makeLayerQueryOpts({
        locale,
        tag,
        layerType: 'tenant',
        l10nLayerMeta,
        l10nLayerPlaceholderData
      })
      const baseLayerQueryOptions = makeLayerQueryOpts({
        locale,
        tag,
        layerType: 'base',
        l10nLayerMeta,
        l10nLayerPlaceholderData
      })

      return [tenantLayerQueryOptions, baseLayerQueryOptions]
    })
    debug('useL10nLayersQueries::layerQueryOptsArr', queryOpts)
    return queryOpts
  }, [l10n_search_locales, tag, l10nLayerMeta, l10nLayerPlaceholderData, placeholderDataReady])

  // Fetch all layers from the api using the query options array constructed above
  return useQueries({ queries: layerQueryOptsArr })
}

function makeLayerQueryOpts({
  locale,
  tag,
  layerType,
  l10nLayerMeta,
  l10nLayerPlaceholderData
}: Omit<L10nLayerParams, 'hash'> & {
  l10nLayerMeta?: L10nLayerMeta
  l10nLayerPlaceholderData?: L10nLayersFromDBRecord
}): UseQueryOptions<
  LocalizationLayer,
  unknown,
  LocalizationLayer,
  [...typeof l10nKeys.layer, string, 'tenant' | 'base', string, string?]
> {
  const layerMetaKey = `l10n_${layerType}_hash` as 'l10n_base_hash' | 'l10n_tenant_hash'
  const hash = l10nLayerMeta?.[layerMetaKey]
  const dataFromDB = l10nLayerPlaceholderData?.[locale]?.[layerType]

  return {
    queryKey: [...l10nKeys.layer, locale, layerType, tag, hash],
    queryFn: (): LocalizationLayer => {
      debug('l10nApi.layer() -> API CALLED...', { locale, layerType, tag, hash: hash! })
      return l10nApi.layer({ locale, layerType, tag, hash: hash! })
    },
    keepPreviousData: true,
    enabled: !!hash,
    placeholderData: dataFromDB,
    initialData: dataFromDB?.hash === hash ? dataFromDB : undefined,
    staleTime: Infinity
  }
}

function makeLayerKey(parts: { layer_type: 'base' | 'tenant'; locale: string; tag: string }) {
  return `${parts.layer_type}-${parts.locale}-${parts.tag}`
}
